import { createSlice } from "@reduxjs/toolkit";
import { getRestrictionData } from "../requests/settings";
import moment from "moment";

export const dayNames = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const setEmptyDay = (day) => ({
  id: null,
  day,
  isActive: false,
  dateStart: "",
  dateEnd: "",
});

const settings = createSlice({
  name: "settings",
  initialState: {
    restrictionsLoading: "success",
    errorFields: [],
    scanLimits: {
      isScanLimits: false,
      uniqueScans: "0",
      errorMessage: "",
    },
    location: {
      isLocation: false,
      isAskLocation: false,
      rule: 1,
      locationMessage: "",
      points: [],
      countries: [],
    },
    password: {
      isPassword: false,
      value: "",
      hintText: "",
    },
    schedule: {
      type: { link: false, file: false },
      id: null,
      timezones: [],
      isSchedule: false,
      timezone: null,
      dateStart: "",
      dateEnd: "",
      contentText: "",
      isShowNothing: true,
      days: [
        [setEmptyDay(dayNames[0])],
        [setEmptyDay(dayNames[1])],
        [setEmptyDay(dayNames[2])],
        [setEmptyDay(dayNames[3])],
        [setEmptyDay(dayNames[4])],
        [setEmptyDay(dayNames[5])],
        [setEmptyDay(dayNames[6])],
      ],
    },
    expiration: {
      isExpirationDate: false,
      expiredDate: "",
    },
  },
  reducers: {
    setIsScanLimits(state, { payload }) {
      state.scanLimits.isScanLimits = payload;
    },
    setUniqueScans(state, { payload }) {
      state.scanLimits.uniqueScans = payload;
    },
    setErrorMessage(state, { payload }) {
      state.scanLimits.errorMessage = payload;
    },
    setIsLocation(state, { payload }) {
      state.location.isLocation = payload;
    },
    setIsAskLocation(state, { payload }) {
      state.location.isAskLocation = payload;
    },
    setRule(state, { payload }) {
      state.location.rule = payload;
    },
    setLocationMessage(state, { payload }) {
      state.location.locationMessage = payload;
    },
    addLocationPoint(
      state,
      { payload: { id, lat, long, range, countryId, cityId, rule } }
    ) {
      state.location.points.push({
        id,
        lat,
        long,
        range,
        countryId,
        cityId,
        rule,
      });
    },
    deleteLocationPoint(state, { payload }) {
      state.location.points = state.location.points.filter(
        (item) => item.id !== payload
      );
    },
    setIsPassword(state, { payload }) {
      state.password.isPassword = payload;
    },
    setPasswordValue(state, { payload }) {
      state.password.value = payload;
    },
    setPasswordHintText(state, { payload }) {
      state.password.hintText = payload;
    },
    setScheduleTimezone(state, { payload }) {
      state.schedule.timezone = payload;
    },
    setIsSchedule(state, { payload }) {
      state.schedule.isSchedule = payload;
    },
    setScheduleDateStart(state, { payload }) {
      state.schedule.dateStart = payload;
    },
    setScheduleDateEnd(state, { payload }) {
      state.schedule.dateEnd = payload;
    },
    setScheduleContentText(state, { payload }) {
      state.schedule.contentText = payload;
    },
    setScheduleIsShowNothing(state, { payload }) {
      state.schedule.isShowNothing = payload;
    },
    addDayItem(state, { payload }) {
      const { isActive } = state.schedule.days[payload][0];
      state.schedule.days[payload].push({
        ...setEmptyDay(dayNames[payload]),
        isActive,
      });
    },
    setToggleActiveDay(state, { payload }) {
      state.schedule.days[payload] = state.schedule.days[payload].map(
        (item) => ({ ...item, isActive: !item.isActive })
      );
    },
    removeDayItem(state, { payload: { dayIndex, fieldIndex } }) {
      state.schedule.days[dayIndex] = state.schedule.days[dayIndex].filter(
        (_, i) => i !== fieldIndex
      );
    },
    resetDayField(state, { payload }) {
      const isActive = state.schedule.days[payload][0].isActive;
      state.schedule.days[payload] = [
        { ...setEmptyDay(dayNames[payload]), isActive },
      ];
    },
    setDayField(
      state,
      { payload: { dayIndex, fieldIndex, valueIndex, value } }
    ) {
      state.schedule.days[dayIndex][fieldIndex][valueIndex] = value;
    },
    applyAll(state) {
      const firstDay = state.schedule.days[0].map((item) => ({
        ...item,
        id: null,
      }));
      state.schedule.days = state.schedule.days.map((item, index) => {
        return index === 0
          ? item
          : [...firstDay.map((el) => ({ ...el, day: dayNames[index] }))];
      });
    },
    editLocationPoint(
      state,
      { payload: { id, lat, long, range, countryId, cityId, rule } }
    ) {
      state.location.points = state.location.points.map((item) =>
        item.id === id
          ? { id, lat, long, range, countryId, cityId, rule }
          : item
      );
    },
    addErrorField(state, { payload }) {
      state.errorFields.push(payload);
    },
    removeErrorField(state, { payload }) {
      state.errorFields = state.errorFields.filter((item) => item !== payload);
    },
    setIsExpirationDate(state, { payload }) {
      state.expiration.isExpirationDate = payload;
    },
    setExpirationDate(state, { payload }) {
      state.expiration.expiredDate = payload;

    },
  },
  extraReducers: {
    [getRestrictionData.fulfilled]: (state, { payload }) => {
      state.scanLimits.isScanLimits = payload.scanLimits.checkbox;
      state.scanLimits.errorMessage = payload.scanLimits.message || "";
      state.scanLimits.uniqueScans = payload.scanLimits.uniqueScans
        ? String(payload.scanLimits.uniqueScans)
        : "0";
      state.schedule.timezones = payload.timezones.map((item) => ({
        id: item.id,
        name: item.name,
        gmt: item.GMT,
      }));

      state.password.isPassword = payload.QRCodePassword.checkbox;
      state.password.value = payload.QRCodePassword.password || "";
      state.password.hintText = payload.QRCodePassword.message || "";
      state.location.isLocation = payload.locationAccess.checkboxLocationAccess;
      state.location.isAskLocation = payload.locationAccess.checkboxAskLocation;
      state.location.locationMessage = payload.locationAccess.message || "";
      state.location.countries = payload.countries.map((item) => ({
        id: item.id,
        country: item.title,
      }));
      // restrictions
      state.schedule.isSchedule = payload.scheduleAssess.checkbox;

      state.schedule.timezone = payload.scheduleAssess.timezone;
      state.schedule.dateStart = payload.scheduleAssess.dateStart?.date
        ? moment(payload.scheduleAssess.dateStart.date).format("YYYY-MM-DD")
        : "";
      state.schedule.dateEnd = payload.scheduleAssess.dateEnd?.date
        ? moment(payload.scheduleAssess.dateEnd.date).format("YYYY-MM-DD")
        : "";
      state.schedule.isShowNothing = payload.scheduleAssess.showNothing;
      state.schedule.id = payload.scheduleAssess.id;
      state.schedule.type = payload.scheduleAssess.types || {
        file: true,
        link: true,
      };

      if (payload.scheduleAssess.schedule.length) {
        state.schedule.days = new Array(7).fill().map((_, index) => {
          const daySchedule = payload.scheduleAssess.schedule.filter(
            (elem) => elem.day === dayNames[index]
          );
          if (daySchedule.length) {
            return daySchedule.map((el) => ({
              id: el.id,
              day: el.day,
              dateStart: el.dateStart || "",
              dateEnd: el.dateEnd || "",
              isActive: el.checkbox,
            }));
          } else {
            return [setEmptyDay(dayNames[index])];
          }
        });
      }
      state.schedule.contentText = payload.scheduleAssess.link || "";
      state.location.points = Object.values(payload.locationAccess.restrictions)
        .flat()
        .map((item) => ({
          id: item.id,
          lat: item.latitude || null,
          long: item.longitude || null,
          range: item.radius || 0,
          countryId: item.country?.id || null,
          cityId: item.city?.id || null,
          rule: item.rule,
        }));
      state.restrictionsLoading = "success";

      state.expiration.isExpirationDate = payload.QRCodeExpired.checkbox;
      state.expiration.expiredDate = payload.QRCodeExpired.expired ?
        moment(payload.QRCodeExpired.expired.date).format("YYYY-MM-DDTHH:mm")
        : "";
      console.log(payload.QRCodeExpired);

    },
    [getRestrictionData.pending]: (state) => {
      state.restrictionsLoading = "load";
    },
    [getRestrictionData.rejected]: (state) => {
      state.restrictionsLoading = "failed";
    },
  },
});
export const {
  setIsScanLimits,
  setUniqueScans,
  setErrorMessage,
  setIsLocation,
  setIsAskLocation,
  setRule,
  setLocationMessage,
  addLocationPoint,
  deleteLocationPoint,
  setIsPassword,
  setPasswordValue,
  setPasswordHintText,
  setScheduleTimezone,
  setScheduleDateStart,
  setScheduleDateEnd,
  setScheduleContentText,
  setScheduleIsShowNothing,
  addDayItem,
  setToggleActiveDay,
  removeDayItem,
  resetDayField,
  setDayField,
  applyAll,
  setIsSchedule,
  editLocationPoint,
  addErrorField,
  removeErrorField,
  setIsExpirationDate,
  setExpirationDate
} = settings.actions;
export default settings.reducer;
